import { Injectable, inject, InjectionToken } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { IRouteConfig } from '../../models/config.models';

// Authentication provider interfaces
export interface IAuthProvider {
  id: string;
  enabled: boolean;
  displayName: string;
  description?: string;
  logoPath?: string; // Path to the provider's logo
  order?: number; // Optional ordering value
}

export interface IEntraIdProvider extends IAuthProvider {
  authority: string;
  clientId: string;
  loginRedirectUri: string;
  logoutRedirectUri: string;
}

export interface IB2CProvider extends IAuthProvider {
  tenant: string;
  clientId: string;
  policy: string;
}

export interface IClientSsoProvider extends IAuthProvider {
  clientId: string;
  provider: string; // The specific SSO provider code
}

// Authentication configuration
export interface IAuthConfig {
  defaultProvider?: string;
  entraId?: IEntraIdProvider;
  azureB2C?: IB2CProvider;
  clientSso?: {
    providers: IClientSsoProvider[];
    defaultProvider?: string;
  };
}

export interface IAzureB2CSettings {
  TenantName: string;
  TenantId: string;
  ClientId: string;
  ClientSecret?: string;
  SignInPolicyName: string;
  Issuers: string[];
}

export interface IAzureAdSettings {
  Instance: string;
  TenantId: string;
  ClientId: string;
  CallbackPath: string;
  RedirectPath: string;
  ClientSecret?: string;
  Issuers: string[];
}

export interface IAppConfig {
  client: string;
  apiUri: string;
  webUri: string;
  env: string;
  favIconPath: string;
  logoPath: string;
  instrumentationKey: string;
  applicationInsightsConnectionString?: string;
  shiftSSOClientId: string;
  shiftSSOAuthority: string;
  shiftSSOLoginRedirectUri: string;
  shiftSSOLogoutRedirectUri: string;
  clientSSOProvider: string;
  clientSSOClientId: string;
  clientSSOUrl: string;
  clientSSOIssuer: string;
  clientSSOAssertUrl: string;
  credentialSSOTenantName: string;
  credentialSSOTenantId: string;
  credentialSSOClientId: string;
  credentialSSOSignInPolicyName: string;
  credentialSSOAuthority: string;
  credentialSSOLoginRedirectUri: string;
  credentialSSOLogoutRedirectUri: string;
  flags: FeatureFlagResponse[];
  AzureB2C?: IAzureB2CSettings;
  AzureAd?: IAzureAdSettings;
  auth?: IAuthConfig;
  // All routes (both dynamic and static module references) controlled by API
  routes?: IRouteConfig[];
  defaultRoute: string;
  customReportMaxTrendDimensions?: number;
  themeVars?: Record<string, string>;
  orgRoles: Record<string, string[]>; // Maps role names to org codes
  clientErrorScreenMessage: string;
}

export const APP_CONFIG = new InjectionToken<IAppConfig>('APP_CONFIG');

export interface FeatureFlagResponse {
    name: string;
    enabled: boolean;
}

export enum FeatureFlagStatus {
    Enabled = 'Enabled',
    Disabled = 'Disabled',
    NotSet = 'NotSet'
}

@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private _config: IAppConfig = inject(APP_CONFIG);

  /**
   * Returns the routingConfig array, falling back to mockRoutingConfig for testing
   */
  get routingConfig(): IRouteConfig[] {
    return this._config.routes || [];
  }

  get config() {
    // Return a normalized config to maintain backward compatibility
    // Ensures auth configuration is always present (merged with defaults)
    return this.getNormalizedConfig();
  }

  /**
   * Returns the authentication configuration
   */
  get auth(): IAuthConfig {
    return this.getNormalizedConfig().auth as IAuthConfig;
  }

  public isInOrgRole(orgKey: string, userRoles: string[]): boolean {
    // Check if the user is in any of the organization roles
    return this._config.orgRoles[orgKey]?.some(role => userRoles.includes(role)) || false;
  }

  /**
   * Gets an array of all enabled authentication providers
   */
  get enabledAuthProviders(): IAuthProvider[] {
    const providers: IAuthProvider[] = [];

    if (this.auth.entraId?.enabled) {
      providers.push(this.auth.entraId);
    }

    if (this.auth.azureB2C?.enabled) {
      providers.push(this.auth.azureB2C);
    }

    if (this.auth.clientSso?.providers) {
      providers.push(...this.auth.clientSso.providers.filter(p => p.enabled));
    }

    // Sort by order if specified
    return providers.sort((a, b) =>
      (a.order !== undefined && b.order !== undefined)
        ? a.order - b.order
        : 0
    );
  }

  /**
   * Gets client SSO provider by ID
   */
  getClientSsoProvider(providerId: string): IClientSsoProvider | undefined {
    return this.auth.clientSso?.providers?.find(p => p.id === providerId);
  }

  /**
   * Creates default auth configuration from environment settings for backward compatibility
   */
  private getDefaultAuthConfig(): IAuthConfig {
        // Create default configuration based on existing environment settings
        const clientSsoProvider = (this._config?.clientSSOProvider || '').trim();
        const clientSsoClientId = (this._config?.clientSSOClientId || '').trim();
        const clientSsoUrl = (this._config?.clientSSOUrl || '').trim();
        const clientSsoIssuer = (this._config?.clientSSOIssuer || '').trim();

        // Client SSO is only valid if we have enough config to execute the flow.
        const hasClientSsoConfig = !!clientSsoClientId
            && (!!clientSsoUrl || !!clientSsoIssuer);

        return {
            entraId: {
                id: 'entraId',
                enabled: true,
                displayName: 'Sign in with Shift SSO',
                description: 'Use your Shift Digital account',
                logoPath: 'assets/images/shiftDigital.png',
                order: 1,
                authority: this._config?.shiftSSOAuthority,
                clientId: this._config?.shiftSSOClientId,
                loginRedirectUri: this._config?.shiftSSOLoginRedirectUri || environment.loginRedirectUrl,
                logoutRedirectUri: this._config?.shiftSSOLogoutRedirectUri || environment.logoutRedirectUrl
            },
            azureB2C: {
                id: 'azureB2C',
                enabled: true,
                displayName: 'Sign in directly',
                description: 'Use your username and password',
                order: 3,
                tenant: this._config?.credentialSSOTenantName || '',
                policy: 'B2C_1_SignIn',
                clientId: this._config?.credentialSSOClientId || '',
            },
            clientSso: {
                providers: hasClientSsoConfig
                    ? [
                        {
                            id: 'clientSSO',
                            enabled: true,
                            displayName: 'Sign in with SSO',
                            description: 'Use your organizations single sign-on',
                            logoPath: `${this._config?.favIconPath || 'assets/images/favicon.ico'}`,
                            order: 2,
                            clientId: clientSsoClientId,
                            provider: clientSsoProvider
                        }
                    ]
                    : [],
                defaultProvider: hasClientSsoConfig ? 'clientSSO' : undefined
            }
        };
    }

  /**
   * Returns a normalized configuration object with sensible defaults merged in.
   * This guarantees that callers accessing `config.auth` will always receive
   * a populated auth configuration even if the backend does not provide one.
   */
  private getNormalizedConfig(): IAppConfig {
    const defaults = this.getDefaultAuthConfig();
    const mergedAuth = this.mergeAuthConfig(this._config.auth, defaults);
    return {
      ...this._config,
      auth: mergedAuth
    } as IAppConfig;
  }

  /**
   * Merge provided auth config (if any) with defaults, preserving explicit values
   * and filling gaps with default values. Does not mutate inputs.
   */
  private mergeAuthConfig(provided: IAuthConfig | undefined, defaults: IAuthConfig): IAuthConfig {
    const entraId = provided?.entraId
      ? { ...defaults.entraId, ...provided.entraId }
      : defaults.entraId;

    const azureB2C = provided?.azureB2C
      ? { ...defaults.azureB2C, ...provided.azureB2C }
      : defaults.azureB2C;

    const providedClientSsoProviders = provided?.clientSso?.providers ?? [];
    const defaultClientSsoProviders = defaults.clientSso?.providers ?? [];

    const clientSsoProviders = providedClientSsoProviders.length
      ? providedClientSsoProviders.map(p => {
          const match = defaultClientSsoProviders.find(d => d.id === p.id);
          return match ? { ...match, ...p } : p;
        })
      : defaultClientSsoProviders;

    const clientSso = {
      providers: clientSsoProviders,
      defaultProvider: provided?.clientSso?.defaultProvider ?? defaults.clientSso?.defaultProvider
    };

    return {
      defaultProvider: provided?.defaultProvider ?? defaults.defaultProvider,
      entraId,
      azureB2C,
      clientSso
    } as IAuthConfig;
  }

    public getFeatureFlagStatus(featureFlagName: string): FeatureFlagStatus {
        const featureFlag = this._config?.flags?.find(flag => flag.name === featureFlagName);
        if (featureFlag) {
            return featureFlag.enabled ? FeatureFlagStatus.Enabled : FeatureFlagStatus.Disabled;
        }
        return FeatureFlagStatus.NotSet;
    }

    public isFeatureFlagEnabled(featureFlagName: string): boolean {
        const featureFlag = this._config?.flags?.find(flag => flag.name === featureFlagName);
        return featureFlag ? featureFlag.enabled : false;
    }
}
